/**
 * Getting Started Articles — Russian
 */

import type { HelpArticle } from "../../../types";

export const GETTING_STARTED_ARTICLES_RU: HelpArticle[] = [
  {
    id: "gs-001",
    slug: "what-is-supabank",
    title: "Что такое Supabank?",
    description: "Узнайте, как Supabank помогает конвертировать крипту в фиат",
    category: "getting-started",
    popular: true,
    order: 1,
    keywords: ["введение", "о сервисе", "обзор", "крипта", "фиат"],
    content: `
# Что такое Supabank?

Supabank — платформа институционального уровня для ликвидации криптовалюты. Конвертируйте крипту в фиат и получайте деньги напрямую на банковский счёт.

## Как это работает

1. **Верификация** — пройдите KYC для доступа ко всем функциям
2. **Создание адреса** — получите уникальный крипто-адрес для депозитов
3. **Отправка крипты** — переведите поддерживаемую криптовалюту на адрес
4. **Автоконвертация** — крипта автоматически конвертируется в фиат
5. **Получение** — деньги приходят на привязанный банковский счёт

## Ключевые возможности

- **Мультичейн** — принимаем депозиты из Ethereum, Polygon, Solana и других сетей
- **Разные валюты** — выплаты в USD, EUR или MXN
- **Мгновенная конвертация** — по конкурентным рыночным курсам
- **Интеграция с банками** — прямые переводы на верифицированный счёт
- **Работа 24/7** — депозиты и конвертация в любое время

## Для кого Supabank?

Supabank подходит:
- Бизнесу, которому нужно конвертировать крипто-выручку в фиат
- Фрилансерам, получающим оплату в крипте
- Трейдерам для эффективного вывода средств
- Компаниям с крипто-казной

## Начало работы

Готовы начать? [Создайте аккаунт](/auth) и пройдите верификацию, чтобы начать конвертировать крипту в фиат.
    `.trim(),
  },
  {
    id: "gs-002",
    slug: "creating-your-account",
    title: "Создание аккаунта",
    description: "Пошаговая инструкция по регистрации в Supabank",
    category: "getting-started",
    popular: true,
    order: 2,
    keywords: ["регистрация", "создать", "аккаунт", "новый"],
    content: `
# Создание аккаунта

Регистрация в Supabank занимает несколько минут.

## Шаг 1: Регистрация

1. Откройте [supabank.xyz](/auth)
2. Введите email
3. Нажмите «Продолжить»
4. Проверьте почту — вам придёт magic-ссылка
5. Перейдите по ссылке для подтверждения

## Шаг 2: Заполнение профиля

После подтверждения email:
1. Введите полное имя (как в документе)
2. Укажите дату рождения
3. Введите адрес проживания
4. Выберите страну

## Шаг 3: Верификация личности (KYC)

Для доступа ко всем функциям:
1. Загрузите документ (паспорт, права или ID-карту)
2. Сделайте селфи для подтверждения
3. Дождитесь одобрения (обычно 2-5 минут)

## Что понадобится

- Действующий email
- Документ с фото государственного образца
- Подтверждение адреса (для некоторых регионов)
- Смартфон или веб-камера для селфи

## Типы аккаунтов

**Личный аккаунт**
- Для персонального использования
- Стандартная верификация
- Начальные лимиты

**Бизнес-аккаунт**
- Для компаний и организаций
- Дополнительные документы
- Повышенные лимиты

## Советы для быстрого одобрения

- Используйте качественное фото документа
- Обеспечьте хорошее освещение для селфи
- Убедитесь, что текст на документах читается
- Имя должно совпадать с документом

## Что дальше

После верификации вы можете:
- Добавить банковский счёт для выплат
- Создать адреса для ликвидации
- Начать получать крипто-депозиты
    `.trim(),
  },
  {
    id: "gs-003",
    slug: "kyc-verification-process",
    title: "Процесс KYC-верификации",
    description: "Всё о верификации личности",
    category: "getting-started",
    popular: true,
    order: 3,
    keywords: ["kyc", "верификация", "личность", "документы", "id"],
    content: `
# Процесс KYC-верификации

KYC (Know Your Customer) — обязательная верификация для использования Supabank. Это помогает соблюдать законодательство и защищать ваш аккаунт.

## Зачем нужна KYC

- Соблюдение законов (AML/KYC)
- Защита от мошенничества
- Безопасность аккаунта
- Повышенные лимиты

## Этапы верификации

### 1. Личные данные
- Полное имя
- Дата рождения
- Адрес проживания
- Гражданство

### 2. Загрузка документа

**Принимаются:**
- Паспорт (рекомендуется)
- Водительские права
- Национальная ID-карта

**Требования к документу:**
- Действующий (не просроченный)
- Чёткое, читаемое фото
- Видны все четыре угла
- Без бликов и отражений

### 3. Селфи-верификация
- Сделайте живое фото лица
- Должно совпадать с фото в документе
- Хорошее освещение
- Без очков и головных уборов

## Сроки верификации

| Статус | Обычное время |
|--------|---------------|
| Автоматическое одобрение | 2-5 минут |
| Ручная проверка | До 24 часов |
| Нужны доп. документы | Зависит от ситуации |

## Частые проблемы

**Документ отклонён?**
- Проверьте срок действия
- Сделайте фото с лучшим освещением
- Убедитесь, что текст читается

**Селфи не прошло?**
- Снимите очки и головной убор
- Смотрите прямо в камеру
- Используйте естественное освещение

## Уровни верификации

| Уровень | Требования | Лимиты |
|---------|------------|--------|
| Базовый | Email | Только просмотр |
| Стандартный | ID + селфи | $50,000/месяц |
| Расширенный | Доп. документы | $500,000/месяц |

## Конфиденциальность

- Документы шифруются при передаче
- Хранятся с ограниченным доступом
- Удаляются после верификации (по политике)
- Никогда не передаются третьим лицам

## Нужна помощь?

Если верификация затягивается — напишите в поддержку.
    `.trim(),
  },
  {
    id: "gs-004",
    slug: "supported-countries",
    title: "Доступные страны и ограничения",
    description: "Где доступны услуги Supabank",
    category: "getting-started",
    order: 4,
    keywords: ["страны", "регионы", "ограничения", "доступность"],
    content: `
# Доступные страны и ограничения

Supabank работает в большинстве стран мира с некоторыми исключениями из-за регуляторных требований.

## Поддерживаемые регионы

Supabank доступен в:
- **Северная Америка**: США (большинство штатов), Канада
- **Европа**: страны ЕС, Великобритания, Швейцария
- **Азия-Тихоокеанский регион**: Япония, Сингапур, Гонконг, Австралия
- **Латинская Америка**: Мексика, Бразилия, Аргентина
- **Прочие**: большинство стран, не указанных как ограниченные

## Ограниченные страны

**Supabank НЕ обслуживает резидентов:**

| Страна | Причина |
|--------|---------|
| Россия | Международные санкции |
| Беларусь | Международные санкции |
| Иран | Санкции OFAC |
| Северная Корея | Санкции OFAC |
| Сирия | Санкции OFAC |
| Куба | Санкции OFAC |
| Венесуэла | Санкции OFAC |
| Мьянма | Санкции OFAC |

## Ограничения в США

**Недоступно в:**
- Нью-Йорк (требования BitLicense)
- Техас (дополнительное лицензирование)

Остальные штаты США поддерживаются.

## Как мы проверяем локацию

При регистрации мы проверяем местоположение через:
- Геолокацию по IP
- Страну выдачи документа
- Верификацию адреса
- Проверку по санкционным спискам OFAC

## Важно

⚠️ **VPN**: Использование VPN для обхода географических ограничений запрещено и приведёт к блокировке аккаунта.

⚠️ **Подделка документов**: Предоставление поддельных документов незаконно и ведёт к перманентному бану.

## Переезд в другую страну?

Если вы переехали:
1. Обновите адрес в профиле
2. Может потребоваться повторная верификация
3. Некоторые функции могут измениться

## Вопросы?

Свяжитесь с поддержкой, если не уверены в доступности в вашем регионе.
    `.trim(),
  },
  {
    id: "gs-005",
    slug: "account-security-basics",
    title: "Основы безопасности аккаунта",
    description: "Базовые практики защиты аккаунта",
    category: "getting-started",
    order: 5,
    keywords: ["безопасность", "пароль", "защита", "безопасно"],
    content: `
# Основы безопасности аккаунта

Защита аккаунта Supabank — ваш приоритет. Следуйте этим рекомендациям, чтобы сохранить средства в безопасности.

## Методы аутентификации

### Magic Link (Email)
- Используем беспарольную аутентификацию
- Безопасная ссылка отправляется на email
- Ссылки истекают через 15 минут
- Одноразовое использование

### Двухфакторная аутентификация (2FA)
- Дополнительный уровень защиты
- Обязательна для крупных операций
- Поддерживает приложения-аутентификаторы

## Лучшие практики безопасности

### Безопасность email
Используйте уникальный email для Supabank
Включите 2FA на почте
Никогда не делитесь ссылками для входа
Не используйте публичные компьютеры

### Безопасность устройств
Обновляйте устройства
Используйте блокировку экрана
Подключайтесь только к надёжным сетям
Не сохраняйте данные входа в браузере

### Распознавание мошенников
Мы никогда не спрашиваем пароль
Мы не просим перевести средства в чате
Проверяйте URL (только supabank.xyz)
Не переходите по подозрительным ссылкам

## Управление сессиями

- Сессии автоматически истекают при неактивности
- Активные сессии видны в настройках
- Можно выйти со всех устройств
- Уведомления о входе с новых устройств

## Как мы вас защищаем

- Сквозное шифрование данных
- Регулярные аудиты безопасности
- Системы мониторинга мошенничества
- Холодное хранение крипто-активов
- Соответствие SOC 2

## Подозрительная активность

Если заметили что-то странное:
1. Сразу свяжитесь с поддержкой
2. Не делитесь данными аккаунта
3. Проверьте недавние транзакции
4. Включите дополнительную 2FA

## Потеряли доступ?

Если потеряли доступ к email:
1. Свяжитесь с поддержкой для верификации
2. Предоставьте альтернативное подтверждение личности
3. Восстановление занимает 24-48 часов
    `.trim(),
  },
  {
    id: "gs-006",
    slug: "two-factor-authentication",
    title: "Двухфакторная аутентификация (2FA)",
    description: "Как настроить и использовать 2FA",
    category: "getting-started",
    order: 6,
    keywords: ["2fa", "двухфакторная", "аутентификация", "безопасность"],
    content: `
# Двухфакторная аутентификация (2FA)

Двухфакторная аутентификация добавляет дополнительный уровень защиты, требуя второе подтверждение при входе.

## Зачем использовать 2FA?

- Защита от несанкционированного доступа
- Обязательна для крупных транзакций
- Стандарт безопасности в индустрии
- Спокойствие за ваши средства

## Поддерживаемые методы 2FA

### Приложение-аутентификатор (рекомендуется)
- Google Authenticator
- Authy
- Microsoft Authenticator
- 1Password

### Email-верификация
- Одноразовые коды на почту
- Резервный вариант, если нет аутентификатора

## Настройка 2FA

### Шаг 1: Включение в настройках
1. Откройте Настройки > Безопасность
2. Нажмите «Включить 2FA»
3. Выберите способ

### Шаг 2: Сканирование QR-кода
1. Откройте приложение-аутентификатор
2. Отсканируйте QR-код
3. Введите 6-значный код для подтверждения

### Шаг 3: Сохранение резервных кодов
- Скачайте резервные коды
- Храните их в безопасном месте офлайн
- Каждый код одноразовый
- Используйте при потере аутентификатора

## Когда требуется 2FA

| Действие | 2FA требуется |
|----------|---------------|
| Вход с нового устройства | Да |
| Смена банковского счёта | Да |
| Крупные выводы | Да |
| Изменения профиля | Иногда |
| Просмотр транзакций | Нет |

## Потеряли устройство с 2FA?

1. Войдите с резервным кодом
2. Откройте Настройки > Безопасность
3. Сбросьте 2FA и настройте заново
4. Если нет резервных кодов — пишите в поддержку

## Рекомендации

Используйте аутентификатор, а не SMS
Сделайте бэкап seed-фразы аутентификатора
Храните резервные коды надёжно
Обновляйте 2FA при смене телефона
Не делайте скриншоты QR-кодов
Не делитесь кодами ни с кем
    `.trim(),
  },
  {
    id: "gs-007",
    slug: "mobile-app-setup",
    title: "Настройка на мобильном",
    description: "Доступ к Supabank с мобильного устройства",
    category: "getting-started",
    order: 7,
    keywords: ["мобильный", "приложение", "ios", "android", "телефон"],
    content: `
# Настройка на мобильном

Используйте Supabank на ходу с нашим мобильным веб-приложением.

## Доступ с мобильного

Supabank — это Progressive Web App (PWA), то есть его можно добавить на главный экран для удобства.

### iOS (Safari)
1. Откройте Safari и перейдите на supabank.xyz
2. Нажмите кнопку «Поделиться» (квадрат со стрелкой)
3. Прокрутите вниз и выберите «На экран Домой»
4. Назовите «Supabank» и нажмите «Добавить»
5. Запускайте с главного экрана

### Android (Chrome)
1. Откройте Chrome и перейдите на supabank.xyz
2. Нажмите меню (три точки)
3. Выберите «Добавить на главный экран»
4. Подтвердите нажатием «Добавить»
5. Запускайте с главного экрана

## Возможности на мобильном

Все функции десктопа доступны:
- Просмотр балансов и транзакций
- Создание адресов ликвидации
- Отслеживание статуса депозитов
- Управление банковскими счетами
- Связь с поддержкой

## Оптимизация под мобильные

- Удобный интерфейс для тачскрина
- Адаптация под маленькие экраны
- Нижняя навигация для быстрого доступа
- Поддержка жестов
- Тёмная тема

## Уведомления

Для получения уведомлений:
1. Разрешите уведомления при запросе
2. Включите в настройках устройства
3. Управляйте в настройках приложения

Типы уведомлений:
- Подтверждение депозитов
- Статус выплат
- Оповещения безопасности
- Важные объявления

## Офлайн-доступ

Некоторые функции работают офлайн:
- Просмотр кэшированной истории
- Доступ к справочным статьям
- Обзор аккаунта

Важно: для транзакций нужен интернет.

## Советы для мобильного

Используйте защищённое подключение
Включите блокировку экрана
Выходите на общих устройствах
Обновляйте браузер
Не используйте публичный Wi-Fi для транзакций
    `.trim(),
  },
  {
    id: "gs-008",
    slug: "desktop-access",
    title: "Доступ с компьютера",
    description: "Использование Supabank на десктопе",
    category: "getting-started",
    order: 8,
    keywords: ["десктоп", "браузер", "компьютер", "веб", "доступ"],
    content: `
# Доступ с компьютера

Полный функционал Supabank доступен в браузере на компьютере.

## Поддерживаемые браузеры

| Браузер | Минимальная версия |
|---------|-------------------|
| Chrome | 90+ |
| Firefox | 88+ |
| Safari | 14+ |
| Edge | 90+ |

## Начало работы

1. Откройте браузер
2. Перейдите на **supabank.xyz**
3. Нажмите «Войти» или «Начать»
4. Введите email для magic-ссылки
5. Проверьте почту и перейдите по ссылке

## Возможности на десктопе

### Полный дашборд
- Полная история транзакций
- Детальная аналитика
- Расширенные фильтры

### Горячие клавиши
- \`Cmd/Ctrl + K\` — быстрый поиск
- \`Cmd/Ctrl + N\` — новая транзакция
- \`Esc\` — закрыть диалоги

### Несколько вкладок
- Открывайте несколько аккаунтов
- Сравнивайте транзакции
- Эффективный рабочий процесс

## Оптимальные настройки экрана

Для лучшего опыта:
- Минимальная ширина: 1280px
- Рекомендуется: 1920x1080 или выше
- Масштаб: 100%

## Настройки браузера

### Включите для лучшей работы
JavaScript
Cookies (для сессии)
Local Storage
Web-уведомления (опционально)

### Приватный режим
Supabank работает в инкогнито, но:
- Придётся входить каждый раз
- Настройки не сохраняются
- 2FA может запрашиваться чаще

## Добавление в закладки

Добавьте Supabank в закладки:
1. Нажмите \`Cmd/Ctrl + D\`
2. Назовите «Supabank»
3. Сохраните на панель закладок
4. Доступ в один клик

## Проблемы

**Страница не загружается?**
- Очистите кэш браузера
- Временно отключите расширения
- Попробуйте другой браузер

**Проблемы со входом?**
- Проверьте папку «Спам»
- Убедитесь, что cookies включены
- Попробуйте режим инкогнито
    `.trim(),
  },
];
